"use client";

import { ComponentProps, useEffect, useState, forwardRef } from 'react';
import { Body } from '@/client/foundations/Typography';
import { InputCheckbox } from '../InputCheckbox/InputCheckbox';
import { InputWrap } from '../InputWrap/InputWrap';

export interface CheckboxGroupProps extends Omit<ComponentProps<typeof InputWrap>, 'children' | 'htmlFor'> {
  /**
   * The main checkbox label.
   * @type string
   */
  groupLabel?: string;
  /**
   * The className of the group.
   * @type string
   */
  className?: string;
  /**
   * The checkbox input children.
   * @type React.ReactElement<typeof InputCheckbox> | React.ReactElement<typeof InputCheckbox>[]
   * @required
   */
  children?: React.ReactElement<typeof InputCheckbox> | React.ReactElement<typeof InputCheckbox>[];
}

const CheckboxesGroup = forwardRef<HTMLElement, CheckboxGroupProps>((props, ref) => {
  const [childrenTypeError, setChildrenTypeError] = useState({
    error: false,
    message: '',
  });

  useEffect(() => {
    const childrenArr = !(props.children instanceof Array) ? [props.children] : props.children;
    childrenArr.forEach(child => {
      if (!child || child.type !== InputCheckbox) {
        const message = `CheckboxesGroup children must be of type InputCheckbox.`;
        console.error(message);
        setChildrenTypeError({ error: true, message });
      }
    });
  }, [props.children]);

  if (childrenTypeError.error) {
    return <Body size="m">{childrenTypeError.message}</Body>;
  }

  return (
    <InputWrap
      label={props.groupLabel}
      assistive={props.assistive}
      required={props.required}
      width={props.width}
      disabled={props.disabled}
      data-test-id={props['data-test-id'] + '-wrapper'}
      className={props.className}
      ref={ref}
    >
      {props.children}
    </InputWrap>
  );
});

CheckboxesGroup.displayName = 'CheckboxesGroup';
export { CheckboxesGroup };
